import {invalidateOnSuccess, omneticApi} from '@dms/api/base';
import {buildBracketSearchParams} from '@dms/api/utils';

import {
  AddRentalPriceGroupItemApiResponse,
  AddRentalPriceGroupItemApiArg,
  CreateRentalPriceGroupApiResponse,
  CreateRentalPriceGroupApiArg,
  ListRentalPriceGroupApiResponse,
  ListRentalPriceGroupApiArg,
  GetRentalPriceGroupApiResponse,
  GetRentalPriceGroupApiArg,
  RemoveRentalPriceGroupApiResponse,
  RemoveRentalPriceGroupApiArg,
  RemoveRentalPriceGroupItemApiResponse,
  RemoveRentalPriceGroupItemApiArg,
  UpdateRentalPriceGroupItemApiResponse,
  UpdateRentalPriceGroupItemApiArg,
  UpdateRentalPriceGroupApiResponse,
  UpdateRentalPriceGroupApiArg,
  ActivateRentalVehicleApiResponse,
  ActivateRentalVehicleApiArg,
  DeactivateRentalVehicleApiResponse,
  DeactivateRentalVehicleApiArg,
  GetRentalVehicleApiResponse,
  GetRentalVehicleApiArg,
  PutRentalVehicleApiResponse,
  PutRentalVehicleApiArg,
  ChangeRentalVehicleBranchApiResponse,
  ChangeRentalVehicleBranchApiArg,
  ListRentalCalendarVehiclesApiResponse,
  ListRentalCalendarVehiclesApiArg,
  ListRentalVehicleBlockApiResponse,
  ListRentalVehicleBlockApiArg,
  CreateRentalVehicleBlockApiResponse,
  CreateRentalVehicleBlockApiArg,
  EditRentalVehicleBlockApiResponse,
  EditRentalVehicleBlockApiArg,
  DeleteRentalVehicleBlockApiResponse,
  DeleteRentalVehicleBlockApiArg,
} from './types';

// The rental calendar feed draws blocks too, so a block change has to invalidate it as well
const getBlockTags = (vehicleId: string) =>
  [
    {type: 'RentalVehicleBlock', id: vehicleId},
    {type: 'RentalOrder', id: 'CALENDAR'},
  ] as const;

const vehicleRentalApi = omneticApi.injectEndpoints({
  endpoints: (build) => ({
    addRentalPriceGroupItem: build.mutation<
      AddRentalPriceGroupItemApiResponse,
      AddRentalPriceGroupItemApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/rental-price-group/${queryArg.rentalPriceGroupId}/item`,
        method: 'POST',
        body: queryArg.rentalPriceGroupItemRequestBody,
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'RentalPriceGroup', id: queryArg.rentalPriceGroupId},
      ],
    }),
    changeRentalVehicleBranch: build.mutation<
      ChangeRentalVehicleBranchApiResponse,
      ChangeRentalVehicleBranchApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/rental-vehicle/${queryArg.vehicleId}/branch`,
        method: 'PUT',
        body: queryArg.changeRentalVehicleBranchRequestBody,
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'RentalVehicle', id: queryArg.vehicleId},
        {type: 'RentalVehicle', id: 'CALENDAR'},
      ],
    }),
    createRentalPriceGroup: build.mutation<
      CreateRentalPriceGroupApiResponse,
      CreateRentalPriceGroupApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/rental-price-group`,
        method: 'POST',
        body: queryArg.createRentalPriceGroupRequestBody,
      }),
    }),
    listRentalPriceGroup: build.query<ListRentalPriceGroupApiResponse, ListRentalPriceGroupApiArg>({
      query: () => ({
        url: `/dms/v1/rental-price-group`,
      }),
    }),
    getRentalPriceGroup: build.query<GetRentalPriceGroupApiResponse, GetRentalPriceGroupApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/rental-price-group/${queryArg.rentalPriceGroupId}`,
      }),
      providesTags: (result, error, queryArg) => [
        {type: 'RentalPriceGroup', id: queryArg.rentalPriceGroupId},
      ],
    }),
    removeRentalPriceGroup: build.mutation<
      RemoveRentalPriceGroupApiResponse,
      RemoveRentalPriceGroupApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/rental-price-group/${queryArg.rentalPriceGroupId}`,
        method: 'DELETE',
      }),
    }),
    removeRentalPriceGroupItem: build.mutation<
      RemoveRentalPriceGroupItemApiResponse,
      RemoveRentalPriceGroupItemApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/rental-price-group/${queryArg.rentalPriceGroupId}/item/${queryArg.rentalPriceGroupItemId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'RentalPriceGroup', id: queryArg.rentalPriceGroupId},
      ],
    }),
    updateRentalPriceGroupItem: build.mutation<
      UpdateRentalPriceGroupItemApiResponse,
      UpdateRentalPriceGroupItemApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/rental-price-group/${queryArg.rentalPriceGroupId}/item/${queryArg.rentalPriceGroupItemId}`,
        method: 'PUT',
        body: queryArg.rentalPriceGroupItemRequestBody,
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'RentalPriceGroup', id: queryArg.rentalPriceGroupId},
      ],
    }),
    updateRentalPriceGroup: build.mutation<
      UpdateRentalPriceGroupApiResponse,
      UpdateRentalPriceGroupApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/rental-price-group/${queryArg.rentalPriceGroupId}`,
        method: 'PUT',
        body: queryArg.updateRentalPriceGroupRequestBody,
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'RentalPriceGroup', id: queryArg.rentalPriceGroupId},
      ],
    }),
    activateRentalVehicle: build.mutation<
      ActivateRentalVehicleApiResponse,
      ActivateRentalVehicleApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/rental-vehicle/${queryArg.vehicleId}/activate`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'RentalVehicle', id: queryArg.vehicleId},
        {type: 'Vehicle', id: queryArg.vehicleId},
        {type: 'RentalVehicle', id: 'CALENDAR'},
      ],
    }),
    deactivateRentalVehicle: build.mutation<
      DeactivateRentalVehicleApiResponse,
      DeactivateRentalVehicleApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/rental-vehicle/${queryArg.vehicleId}/deactivate`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'RentalVehicle', id: queryArg.vehicleId},
        {type: 'Vehicle', id: queryArg.vehicleId},
        {type: 'RentalVehicle', id: 'CALENDAR'},
      ],
    }),
    getRentalVehicle: build.query<GetRentalVehicleApiResponse, GetRentalVehicleApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/rental-vehicle/${queryArg.vehicleId}`,
      }),
      providesTags: (result, error, queryArg) => [{type: 'RentalVehicle', id: queryArg.vehicleId}],
    }),
    putRentalVehicle: build.mutation<PutRentalVehicleApiResponse, PutRentalVehicleApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/rental-vehicle/${queryArg.vehicleId}`,
        method: 'PUT',
        body: queryArg.rentalVehicleRequestBody,
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'RentalVehicle', id: queryArg.vehicleId},
        {type: 'RentalVehicle', id: 'CALENDAR'},
      ],
    }),
    listRentalCalendarVehicles: build.query<
      ListRentalCalendarVehiclesApiResponse,
      ListRentalCalendarVehiclesApiArg
    >({
      query: (queryArg) => ({
        // Multiselect filters must reach the wire as bracket arrays, which fetchBaseQuery
        // cannot produce from `params` (it comma-joins arrays)
        url: `/dms/v1/rental-vehicle/calendar?${buildBracketSearchParams(queryArg)}`,
      }),
      providesTags: [{type: 'RentalVehicle', id: 'CALENDAR'}],
    }),
    listRentalVehicleBlock: build.query<
      ListRentalVehicleBlockApiResponse,
      ListRentalVehicleBlockApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/rental-vehicle/${queryArg.vehicleId}/block`,
      }),
      providesTags: (result, error, queryArg) => [
        {type: 'RentalVehicleBlock', id: queryArg.vehicleId},
      ],
    }),
    createRentalVehicleBlock: build.mutation<
      CreateRentalVehicleBlockApiResponse,
      CreateRentalVehicleBlockApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/rental-vehicle/${queryArg.vehicleId}/block`,
        method: 'POST',
        body: queryArg.createRentalVehicleBlockRequestBody,
      }),
      invalidatesTags: invalidateOnSuccess((_result, queryArg: CreateRentalVehicleBlockApiArg) =>
        getBlockTags(queryArg.vehicleId)
      ),
    }),
    editRentalVehicleBlock: build.mutation<
      EditRentalVehicleBlockApiResponse,
      EditRentalVehicleBlockApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/rental-vehicle/${queryArg.vehicleId}/block/${queryArg.blockId}`,
        method: 'PUT',
        body: queryArg.editRentalVehicleBlockRequestBody,
      }),
      invalidatesTags: invalidateOnSuccess((_result, queryArg: EditRentalVehicleBlockApiArg) =>
        getBlockTags(queryArg.vehicleId)
      ),
    }),
    deleteRentalVehicleBlock: build.mutation<
      DeleteRentalVehicleBlockApiResponse,
      DeleteRentalVehicleBlockApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/rental-vehicle/${queryArg.vehicleId}/block/${queryArg.blockId}`,
        method: 'DELETE',
      }),
      invalidatesTags: invalidateOnSuccess((_result, queryArg: DeleteRentalVehicleBlockApiArg) =>
        getBlockTags(queryArg.vehicleId)
      ),
    }),
  }),
  overrideExisting: false,
});

export const {
  useChangeRentalVehicleBranchMutation,
  useAddRentalPriceGroupItemMutation,
  useCreateRentalPriceGroupMutation,
  useListRentalPriceGroupQuery,
  useGetRentalPriceGroupQuery,
  useRemoveRentalPriceGroupMutation,
  useRemoveRentalPriceGroupItemMutation,
  useUpdateRentalPriceGroupItemMutation,
  useUpdateRentalPriceGroupMutation,
  useActivateRentalVehicleMutation,
  useDeactivateRentalVehicleMutation,
  useGetRentalVehicleQuery,
  usePutRentalVehicleMutation,
  useListRentalCalendarVehiclesQuery,
  useListRentalVehicleBlockQuery,
  useCreateRentalVehicleBlockMutation,
  useEditRentalVehicleBlockMutation,
  useDeleteRentalVehicleBlockMutation,
} = vehicleRentalApi;
